import * as ToastPrimitives from '@radix-ui/react-toast'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/utils'
import { Icon } from './icon'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 left-1/2 -translate-x-1/2 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-auto sm:flex-col md:max-w-[420px] md:mt-[80px]',
      className,
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center space-x-4 overflow-hidden rounded-md border p-3 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top-full data-[state=open]:slide-in-from-top-full',
  {
    variants: {
      variant: {
        default: 'border bg-background-inverse text-header text-header-inverse border-border-inverse backdrop-blur',
        destructive: 'destructive group border-error bg-error/50 text-error',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)
const iconVariants = cva('text-header-inverse rounded-full flex items-center justify-center stroke-2 size-10 p-2', {
  variants: {
    icon: {
      default: 'bg-primary-accent',
      destructive: 'bg-error',
      info: 'bg-warning',
      loading: 'animate-spin',
    },
  },
  defaultVariants: {
    icon: 'default',
  },
})

const ToastRoot = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> &
    VariantProps<typeof iconVariants>
>(({ className, variant, icon, children, ...props }, ref) => {
  const iconName =
    icon === 'destructive' ? 'X' : icon === 'info' ? 'Info' : icon === 'loading' ? 'LoaderCircle' : 'Check'

  return (
    <ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props}>
      <div className="w-[48px] h-[48px] flex items-center justify-center">
        <Icon name={iconName} className={iconVariants({ icon })} />
      </div>
      {children}
    </ToastPrimitives.Root>
  )
})
ToastRoot.displayName = ToastPrimitives.Root.displayName

const toastActionVariants = cva(
  'inline-flex shrink-0 items-center justify-center text-sm font-medium ring-offset-background transition-colors focus:outline-none focus:ring-2 focus:ring-border-inverse focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        button:
          'h-8 rounded-md border bg-transparent px-3 hover:bg-secondary group-[.destructive]:border-sub-surface-bg/40 group-[.destructive]:hover:border-error/30 group-[.destructive]:hover:bg-error group-[.destructive]:hover:text-error-foreground group-[.destructive]:focus:ring-error',
        link: 'h-auto bg-transparent p-0 text-primary-accent underline hover:opacity-80',
      },
    },
    defaultVariants: {
      variant: 'button',
    },
  },
)

type ToastActionProps = React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> &
  VariantProps<typeof toastActionVariants>

const ToastAction = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Action>, ToastActionProps>(
  ({ className, variant, ...props }, ref) => (
    <ToastPrimitives.Action ref={ref} className={cn(toastActionVariants({ variant }), className)} {...props} />
  ),
)
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 text-header-inverse opacity-0 transition-opacity hover:text-header-inverse/80 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-error group-[.destructive]:hover:text-error group-[.destructive]:focus:ring-error group-[.destructive]:focus:ring-offset-error',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn('text-sm text-secondary', className)} {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof ToastRoot>

type ToastActionElement = React.ReactElement<ToastActionProps>

export {
  ToastAction,
  type ToastActionElement,
  ToastClose,
  ToastDescription,
  type ToastProps,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
}
